/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { createContext, useCallback, useContext } from "react";

import { defaultTranslations } from "../i18n/defaults";

type TranslationParams = Record<string, string | number>;
type TranslationFn = (key: string, params?: TranslationParams) => string;

const interpolate = (template: string, params?: TranslationParams): string => {
  if (!params) return template;
  return Object.entries(params).reduce(
    (result, [key, value]) =>
      result.replace(new RegExp(`{{\\s*${key}\\s*}}`, "g"), String(value)),
    template,
  );
};

const PlayerTranslationContext = createContext<TranslationFn>(
  (key: string, params?: TranslationParams) =>
    interpolate(defaultTranslations[key] || key, params),
);

export const PlayerTranslationProvider: React.FC<{
  t?: TranslationFn;
  children: React.ReactNode;
}> = ({ t, children }) => {
  const translate = useCallback(
    (key: string, params?: TranslationParams) => {
      if (t) return t(key, params);
      return interpolate(defaultTranslations[key] || key, params);
    },
    [t],
  );

  return (
    <PlayerTranslationContext.Provider value={translate}>
      {children}
    </PlayerTranslationContext.Provider>
  );
};

export const usePlayerTranslation = (): { t: TranslationFn } => {
  const translate = useContext(PlayerTranslationContext);
  return { t: translate };
};
